// Page: UserManagement
// Purpose: Admin dashboard for user cohorts and subscription management. Loads
// dashboard data, provides section-specific sorting, enterprise filtering, and
// actions to sync/cancel subscriptions and delete users.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import Footer from '../../../components/shared/Footer';
import { 
  ArrowLeft, Users, Crown, Shield, Calendar, 
  RefreshCw, Gift, Building2, UserPlus, TrendingUp, Clock, Trash2, UserCheck
} from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { UserManagementSkeleton } from '../../../components/ui/feedback/skeleton.jsx';
import api from '../../../api';
import adminApi from '../../../api/accounts/adminApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import {
  UserSection,
  useUserManagement,
  UserSubscriptionSyncModal,
  CancelSubscriptionModal,
  DeleteUserModal,
  UserDemographics
} from '../../../components/admin';

export default function UserManagement() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Sorting state - now section-specific
  const [sectionSorting, setSectionSorting] = useState({
    // Default sorting for each section
    newPremiumUsers: { field: 'lastActive', direction: 'desc' },
    trialPremiumUsers: { field: 'trialEndDate', direction: 'asc' }, // Default to Time Until End, ascending (soonest first)
    activeUsers: { field: 'lastActive', direction: 'desc' }, // Default to most recently active first
    payingPremiumUsers: { field: 'nextDecisionDate', direction: 'asc' }, // Default to next decision date, soonest first
    nonRenewingUsers: { field: 'nextDecisionDate', direction: 'asc' }, // Default to next decision date, soonest first
    enterpriseUsers: { field: 'enterprise', direction: 'asc' }, // Group by enterprise name A→Z
    newCustomers: { field: 'lastActive', direction: 'desc' },
    freeUsers: { field: 'lastActive', direction: 'desc' },
    formerSubscribers: { field: 'lastActive', direction: 'desc' },
    formerTrialUsers: { field: 'lastActive', direction: 'desc' },
    orphanedUsers: { field: 'lastActive', direction: 'desc' },
    deletedUsers: { field: 'lastActive', direction: 'desc' },
    basicUsers: { field: 'lastActive', direction: 'desc' }
  });
  
  // User data state
  const [userData, setUserData] = useState({
    summary: {
      totalUsers: 0,
      totalPremiumUsers: 0,
      totalBasicUsers: 0,
      totalCustomTemplates: 0,
      totalNamespaces: 0,
      totalDeletedUsers: 0,
      timestamp: null
    },
        users: [],
    migratedBackUsers: [],
    orphanedUsers: [],
    deletedUsers: []
   });
  
  // Enterprise data and filtering state
  const [enterprises, setEnterprises] = useState([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState(null);
  
  // Modal states
  const [syncModalOpenFor, setSyncModalOpenFor] = useState(null);
  const [cancelModalFor, setCancelModalFor] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  
  // Deleted user modal states
  const [showDeletedUserModal, setShowDeletedUserModal] = useState(false);
  const [deletedUserToDelete, setDeletedUserToDelete] = useState(null);
  const [isDeletingDeletedUser, setIsDeletingDeletedUser] = useState(false);

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };
  
  // Get the auth context
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Check if user is the admin
  const isAdmin = isUserAdmin(user);
  
  // Use the custom hook for user management logic
  const {
    collapsedSections,
    toggleSectionCollapse,
    isNewPremiumUser,
    isTrialPremiumUser,
    isPayingPremiumUser,
    isNonRenewingPayingUser,
    isActiveUser,
    isFreeUser,
    isNewCustomer,
    isFormerSubscriber,
    isFormerTrialUser,
    isBasicUser,
    isOrphanedUser,
    getEnterpriseNameForUser,
    getEnterpriseMetaForUser,
    getCancellationBadgeInfo,
    formatDate,
    formatExactDateTime,
    timeAgo,
    getTrialEndDate,
    formatSubscriptionEndDate,
    getSubscriptionColor,
    filteredUsers
  } = useUserManagement(userData, enterprises);

  // Sort users function - now section-specific
  const sortUsers = (users, sectionKey = 'default') => {
    const sorting = sectionSorting[sectionKey] || { field: 'lastActive', direction: 'desc' };
    const { field: sortField, direction: sortDirection } = sorting;
    
    return [...users].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'phone': {
          const normalize = (u) => {
            const raw = u.phone?.e164 || u.phone?.national || '';
            const digits = String(raw).replace(/\D/g, '');
            if (!digits) return { has: false, area: '', rest: '' };
            // Treat 11-digit US numbers (1XXXXXXXXXX) and 10-digit equally; extract area code
            const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
            const area = d.slice(0, 3);
            const rest = d.slice(3);
            return { has: true, area, rest };
          };
          const A = normalize(a);
          const B = normalize(b);
          // Users with phones first
          if (A.has !== B.has) return A.has ? -1 : 1;
          aValue = `${A.area}-${A.rest}`;
          bValue = `${B.area}-${B.rest}`;
          break;
        }
        case 'role': {
          aValue = (a.role || '').toLowerCase();
          bValue = (b.role || '').toLowerCase();
          break;
        }
        case 'displayName':
          aValue = (a.displayName || a.email || '').toLowerCase();
          bValue = (b.displayName || b.email || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          bValue = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          break;
        case 'lastActive':
          aValue = a.lastActive?.toDate ? a.lastActive.toDate() : new Date(a.lastActive || 0);
          bValue = b.lastActive?.toDate ? b.lastActive.toDate() : new Date(b.lastActive || 0);
          break;
        case 'planInterval':
          aValue = (a.planInterval || a.subscriptionInterval || '').toLowerCase();
          bValue = (b.planInterval || b.subscriptionInterval || '').toLowerCase();
          break;
        // Activity-based sorting (numeric values)
        case 'campaignsCreated':
          aValue = a.campaignsCreated || 0;
          bValue = b.campaignsCreated || 0;
          break;
        case 'surveyVideosCollected':
          aValue = a.surveyVideosCollected || 0;
          bValue = b.surveyVideosCollected || 0;
          break;
        case 'totalTemplatesEdited':
          aValue = a.totalTemplatesEdited || 0;
          bValue = b.totalTemplatesEdited || 0;
          break;
        case 'totalVideoTemplatesEdited':
          aValue = a.totalVideoTemplatesEdited || 0;
          bValue = b.totalVideoTemplatesEdited || 0;
          break;
        case 'liveSupportClicks':
          aValue = a.liveSupportClicks || 0;
          bValue = b.liveSupportClicks || 0;
          break;
        case 'bookCalendlyClicks':
          aValue = a.bookCalendlyClicks || 0;
          bValue = b.bookCalendlyClicks || 0;
          break;
        case 'visitRobocentClicks':
          aValue = a.visitRobocentClicks || 0;
          bValue = b.visitRobocentClicks || 0;
          break;
        case 'totalStockVideosDownloaded':
          aValue = a.totalStockVideosDownloaded || 0;
          bValue = b.totalStockVideosDownloaded || 0;
          break;
        case 'customTemplatesCreated':
          aValue = a.customTemplatesCreated || 0;
          bValue = b.customTemplatesCreated || 0;
          break;
        case 'namespacesCount':
          aValue = a.namespacesCount || 0;
          bValue = b.namespacesCount || 0;
          break;
        case 'totalTemplatePageHits':
          aValue = a.totalTemplatePageHits || 0;
          bValue = b.totalTemplatePageHits || 0;
          break;
        case 'activeDays':
          aValue = a.activeDays || 0;
          bValue = b.activeDays || 0;
          break;
        case 'monthlyPrice':
          aValue = a.monthlyPrice || 0;
          bValue = b.monthlyPrice || 0;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue || 0;
          bValue = b.totalRevenue || 0;
          break;
        case 'monthsAsPayingCustomer':
          aValue = a.monthsAsPayingCustomer || 0;
          bValue = b.monthsAsPayingCustomer || 0;
          break;
        case 'trialEndDate':
          // Calculate trial end date from user data
          aValue = getTrialEndDate(a) || new Date(0);
          bValue = getTrialEndDate(b) || new Date(0);
          break;
        case 'subscriptionStatus':
          aValue = (a.subscriptionStatus || 'none').toLowerCase();
          bValue = (b.subscriptionStatus || 'none').toLowerCase();
          break;
        case 'subscriptionCancelAt':
          aValue = a.subscriptionCancelAt?.toDate ? a.subscriptionCancelAt.toDate() : new Date(a.subscriptionCancelAt || 0);
          bValue = b.subscriptionCancelAt?.toDate ? b.subscriptionCancelAt.toDate() : new Date(b.subscriptionCancelAt || 0);
          break;
        case 'nextDecisionDate':
          // Calculate next decision date for sorting (payment or cancellation)
          const getDecisionDateForSorting = (user) => {
            let decisionDate = null;
            const now = new Date();
            
            // Priority 1: If user has scheduled cancellation, use subscriptionPeriodEnd
            if (user.subscriptionCancelAt && user.subscriptionPeriodEnd) {
              decisionDate = user.subscriptionPeriodEnd?.toDate ? user.subscriptionPeriodEnd.toDate() : new Date(user.subscriptionPeriodEnd);
            }
            // Priority 2: If user has active subscription, next billing is the decision date
            else if (user.subscriptionPeriodEnd) {
              decisionDate = user.subscriptionPeriodEnd?.toDate ? user.subscriptionPeriodEnd.toDate() : new Date(user.subscriptionPeriodEnd);
            }
            // Priority 3: If user is in trial, trial end is the decision date
            else if (user.trialEnd) {
              decisionDate = user.trialEnd?.toDate ? user.trialEnd.toDate() : new Date(user.trialEnd);
            }
            
            // If we have a date but it's in the past, calculate the next billing cycle
            if (decisionDate && decisionDate < now) {
              const interval = user.planInterval || user.subscriptionInterval || 'month';
              const isAnnual = interval.toLowerCase().includes('year') || interval.toLowerCase().includes('annual');
              
              const monthsToAdd = isAnnual ? 12 : 1;
              let nextDate = new Date(decisionDate);
              
              // Keep adding billing cycles until we get a future date
              while (nextDate < now) {
                nextDate.setMonth(nextDate.getMonth() + monthsToAdd);
              }
              
              decisionDate = nextDate;
            }
            
            return decisionDate || new Date(0); // Far past for users without decision dates
          };
          
          const aDecisionDate = getDecisionDateForSorting(a);
          const bDecisionDate = getDecisionDateForSorting(b);
          const nowDecision = new Date();
          
          // Calculate minutes until decision date (negative if expired)
          aValue = Math.floor((aDecisionDate - nowDecision) / (1000 * 60));
          bValue = Math.floor((bDecisionDate - nowDecision) / (1000 * 60));
          break;
        case 'enterprise':
          aValue = (getEnterpriseNameForUser(a.email, a) || '').toLowerCase();
          bValue = (getEnterpriseNameForUser(b.email, b) || '').toLowerCase();
          break;
        default:
          aValue = a.lastActive?.toDate ? a.lastActive.toDate() : new Date(a.lastActive || 0);
          bValue = b.lastActive?.toDate ? b.lastActive.toDate() : new Date(b.lastActive || 0);
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle sorting - now section-specific
  const handleSort = (field, sectionKey) => {
    setSectionSorting(prev => {
      const currentSorting = prev[sectionKey] || { field: 'lastActive', direction: 'desc' };
      
      if (currentSorting.field === field) {
        // Same field, toggle direction
        return {
          ...prev,
          [sectionKey]: {
            field,
            direction: currentSorting.direction === 'asc' ? 'desc' : 'asc'
          }
        };
      } else {
        // Different field, set to desc by default
        return {
          ...prev,
          [sectionKey]: {
            field,
            direction: 'desc'
          }
        };
      }
    });
  };

  // Get sort icon - now section-specific
  const getSortIcon = (field, sectionKey) => {
    const sorting = sectionSorting[sectionKey] || { field: 'lastActive', direction: 'desc' };
    if (sorting.field !== field) return null;
    return sorting.direction === 'asc' ? '↑' : '↓';
  };

  // Filter users by enterprise if selected
  const getFilteredUsers = () => {
    if (!selectedEnterpriseId) return filteredUsers;
    
    const selectedEnterprise = enterprises.find(ent => ent.id === selectedEnterpriseId);
    if (!selectedEnterprise) return filteredUsers;
    
    return filteredUsers.filter(user => {
      const emailMatches = user.email && selectedEnterprise.enterpriseDomain && user.email.toLowerCase().endsWith(`@${selectedEnterprise.enterpriseDomain.toLowerCase()}`);
      const flagMatches = user.isEnterpriseUser === true || user.enterpriseId === selectedEnterprise.id;
      return (emailMatches || flagMatches);
    });
  };

  // Clear enterprise filter
  const clearEnterpriseFilter = () => {
    setSelectedEnterpriseId(null);
  };

  // Handle subscription sync (from modal)
  const handleSyncSubscription = async ({ subscriptionId, customerId }) => {
    try {
      const targetUser = syncModalOpenFor;
      if (!targetUser?.uid) {
        addToast('No user selected for subscription sync', 'error');
        return;
      }
      const response = await adminApi.attachAndSyncSubscription(
        targetUser.uid,
        subscriptionId || null,
        customerId || null
      );

      if (response?.subscription) {
        addToast(
          `Subscription sync complete for ${targetUser.email}${response?.subscription?.status ? ` (${response.subscription.status})` : ''}`,
          'success'
        );
        await loadUserData();
      } else {
        addToast(`No subscription found for ${targetUser.email}`, 'info');
      }
    } catch (error) {
      console.error('Error syncing subscription:', error);
      addToast(
        `Error syncing subscription for ${syncModalOpenFor?.email || 'user'}: ${error.message}`,
        'error'
      );
    } finally {
      setSyncModalOpenFor(null);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async (user) => {
    try {
      await adminApi.cancelSubscription(user.uid);
      addToast(`Cancelled subscription for ${user.email} and downgraded to basic.`, 'success');
      await loadUserData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      addToast(`Error cancelling subscription for ${user.email}: ${error.message}`, 'error');
    } finally {
      setCancelModalFor(null);
    }
  };

  // Handle delete user
  const handleDeleteUserClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;

    setIsDeletingUser(true);
    try {
      const result = await adminApi.deleteUser(userToDelete.uid);
      addToast(
        `User ${userToDelete.email} has been deleted. Deleted: ${result.stats?.namespacesDeleted || 0} namespaces, ${result.stats?.campaignsDeleted || 0} campaigns, ${result.stats?.videosDeleted || 0} videos.`,
        'success'
      );
      await loadUserData(); // Refresh the data
    } catch (error) {
      console.error('Error deleting user:', error);
      addToast(`Error deleting user: ${error.message}`, 'error');
    } finally {
      setIsDeletingUser(false);
    setShowDeleteModal(false);
    setUserToDelete(null);
    }
  };

  // Handle delete deleted user record - show confirmation modal
  const handleDeleteDeletedUser = (deletedUserId, originalUserId, deletedUserData) => {
    setDeletedUserToDelete({ 
      id: deletedUserId, 
      originalUserId, 
      ...deletedUserData 
    });
    setShowDeletedUserModal(true);
  };

  // Actually delete the deleted user record after confirmation
  const confirmDeleteDeletedUser = async () => {
    if (!deletedUserToDelete) return;
    
    setIsDeletingDeletedUser(true);
    try {
      await adminApi.deleteDeletedUserRecord(deletedUserToDelete.id);
      addToast('Deleted user record removed from dashboard', 'success');
      await loadUserData(); // Refresh the data
    } catch (error) {
      console.error('Error deleting deleted user record:', error);
      addToast(`Error removing deleted user record: ${error.message}`, 'error');
    } finally {
      setIsDeletingDeletedUser(false);
      setShowDeletedUserModal(false);
      setDeletedUserToDelete(null);
    }
  };

  // Load user data
  const loadUserData = async () => {
    try {
      setIsLoading(true);
      console.log('UserManagement - Loading user data...');
      
      const response = await api.get('/admin/users/dashboard', { timeout: 30000 }); // 30 second timeout for dashboard
      console.log('UserManagement - API response:', response);
      
             if (response && response.users) {
         setUserData(response);
         setError(null);
       } else {
        throw new Error('Invalid response format from API');
      }
         } catch (error) {
       console.error('UserManagement - Error loading user data:', error);
       setError(error.message);
     } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Load enterprises data
  const loadEnterprises = async () => {
    try {
      console.log('UserManagement - Loading enterprises...');
      const response = await api.get('/admin/enterprises');
      const list = Array.isArray(response)
        ? response
        : (Array.isArray(response?.enterprises) ? response.enterprises : []);
      setEnterprises(list);
      console.log('UserManagement - Loaded enterprises:', list);
    } catch (error) {
      console.error('UserManagement - Error loading enterprises:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (!isAdmin) {
      navigate('/app');
      return;
    }
    
    loadUserData();
    loadEnterprises();
  }, [isAdmin, navigate]);

  // Redirect if not admin
  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <UserManagementSkeleton />
      </div>
    );
  }

  // Get filtered users
  const displayUsers = getFilteredUsers();

  // User group calculations with section-specific sorting (excluding free users from all sections except the dedicated free users section)
  const newPremiumUsers = sortUsers(
    displayUsers.filter(user => isNewPremiumUser(user) && !isFreeUser(user)), 
    'newPremiumUsers'
  );
  const trialPremiumUsers = sortUsers(
    displayUsers.filter(user => isTrialPremiumUser(user) && !isNewCustomer(user) && !isFreeUser(user) && user.migratedBackToLegacy !== true), 
    'trialPremiumUsers'
  );
  const migratedBackUsers = sortUsers(
    displayUsers.filter(user => user.migratedBackToLegacy === true && isTrialPremiumUser(user)),
    'migratedBackUsers'
  );
  const activeUsers = sortUsers(
    displayUsers.filter(user => isActiveUser(user) && !isFreeUser(user)), 
    'activeUsers'
  );
  const payingPremiumUsers = sortUsers(
    displayUsers.filter(user => isPayingPremiumUser(user) && !isNewCustomer(user) && !isFreeUser(user) && !isNonRenewingPayingUser(user) && !isActiveUser(user)), 
    'payingPremiumUsers'
  );
  const nonRenewingUsers = sortUsers(
    displayUsers.filter(user => isNonRenewingPayingUser(user) && !isFreeUser(user)), 
    'nonRenewingUsers'
  );
  // Treat migrated-back trial users as paying for counts while trial is active
  const trialMigrationUsers = displayUsers.filter(user => user.migratedBackToLegacy === true && isTrialPremiumUser(user));
  const enterpriseUsers = sortUsers(
    displayUsers.filter(user => (getEnterpriseNameForUser(user.email, user) || user.isEnterpriseUser || user.enterpriseId)), 
    'enterpriseUsers'
  );
  const newCustomers = sortUsers(
    displayUsers.filter(user => 
      isNewCustomer(user) && !isPayingPremiumUser(user) && !isTrialPremiumUser(user) && !isFreeUser(user)
    ), 
    'newCustomers'
  );
  const freeUsers = sortUsers(
    displayUsers.filter(user => 
      isFreeUser(user) && 
      !(getEnterpriseNameForUser(user.email, user) || user.isEnterpriseUser || user.enterpriseId)
    ), 
    'freeUsers'
  ); // Only free users section includes free users
  const formerSubscribers = sortUsers(
    displayUsers.filter(user => isFormerSubscriber(user) && !isFreeUser(user)), 
    'formerSubscribers'
  );
  const formerTrialUsers = sortUsers(
    displayUsers.filter(user => isFormerTrialUser(user) && !isFreeUser(user)), 
    'formerTrialUsers'
  );
  const basicUsers = sortUsers(
    displayUsers.filter(user => isBasicUser(user) && !isFreeUser(user) && !isNewCustomer(user)), 
    'basicUsers'
  );
  const orphanedUsers = sortUsers(
    (userData.orphanedUsers || []).filter(user => !isFreeUser(user)), 
    'orphanedUsers'
  ); // Exclude free users from orphaned as well and apply sorting
  const deletedUsers = sortUsers(
    userData.deletedUsers || [], 
    'deletedUsers'
  ); // Get deleted users from API response and apply sorting

  // Common user row props
  const userRowProps = {
    getCancellationBadgeInfo,
    isNewCustomer,
    getEnterpriseNameForUser,
    getSubscriptionColor,
    formatDate,
    formatExactDateTime,
    timeAgo,
    getTrialEndDate,
    formatSubscriptionEndDate,
    onSyncSubscription: setSyncModalOpenFor,
    onCancelSubscription: setCancelModalFor,
    onDeleteUser: handleDeleteUserClick
  };

  return (
    <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto pb-20">
      {/* Back to Admin Users button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/users')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to User Management
        </button>
      </div>

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
            <Users className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">User Management</h1>
          </div>
        <div className="mb-4">
          <p className="text-gray-600 dark:text-gray-400 mb-3 sm:mb-0">
            Comprehensive overview of all users, their subscription status, and activity metrics.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4">
            <button
              onClick={() => { setIsRefreshing(true); loadUserData(); }}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent backdrop-blur-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/30 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm transition-colors w-full sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* User Demographics */}
      <UserDemographics
        displayUsers={displayUsers}
        userData={userData}
        newPremiumUsers={newPremiumUsers}
        trialPremiumUsers={trialPremiumUsers}
        trialMigrationUsers={trialMigrationUsers}
        activeUsers={activeUsers}
        enterpriseUsers={enterpriseUsers}
        newCustomers={newCustomers}
        basicUsers={basicUsers}
        formerSubscribers={formerSubscribers}
        formerTrialUsers={formerTrialUsers}
        freeUsers={freeUsers}
        nonRenewingUsers={nonRenewingUsers}
        isPayingPremiumUser={isPayingPremiumUser}
        isTrialPremiumUser={isTrialPremiumUser}
        isNonRenewingPayingUser={isNonRenewingPayingUser}
        isFreeUser={isFreeUser}
        getTrialEndDate={getTrialEndDate}
      />



        {/* User Sections */}
        <div className="space-y-8">
        {/* Note: Enterprise section moved below Non-Renewing Users per request */}
          {/* New Premium Users Section - Priority */}
          <UserSection
            sectionKey="newPremiumUsers"
            icon={<Crown className="h-6 w-6 text-green-500" />}
            title="New Premium Users"
            users={newPremiumUsers}
            description="Premium users who joined today in CST timezone (sorted by join date, newest first)"
            color="green"
            isCollapsed={collapsedSections.newPremiumUsers}
            onToggle={() => toggleSectionCollapse('newPremiumUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'newPremiumUsers')}
            handleSort={(field) => handleSort(field, 'newPremiumUsers')}
          />

          {/* Trial Premium Users Section - Priority */}
          <UserSection
            sectionKey="trialPremiumUsers"
            icon={<Shield className="h-6 w-6 text-blue-500" />}
            title="Trial Premium Users"
            users={trialPremiumUsers}
            description="Existing premium trial users (excludes new users from today, sorted by trial end date - soonest first)"
            color="blue"
            isCollapsed={collapsedSections.trialPremiumUsers}
            onToggle={() => toggleSectionCollapse('trialPremiumUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: true,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'trialPremiumUsers')}
            handleSort={(field) => handleSort(field, 'trialPremiumUsers')}
          />

          {/* Migrated Back to Legacy Users Section - Priority */}
          <UserSection
            sectionKey="migratedBackUsers"
            icon={<RefreshCw className="h-6 w-6 text-orange-500" />}
            title="Migrated Back To Legacy (Trialing)"
            users={migratedBackUsers}
            description="Trialing users migrated from FULL_PAC back to Legacy; once trial ends they move to their normal category."
            color="orange"
            isCollapsed={collapsedSections.migratedBackUsers}
            onToggle={() => toggleSectionCollapse('migratedBackUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: true,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'migratedBackUsers')}
            handleSort={(field) => handleSort(field, 'migratedBackUsers')}
          />

          {/* Active Users Section - High Priority */}
          <UserSection
            sectionKey="activeUsers"
            icon={<Clock className="h-6 w-6 text-green-500" />}
            title="Currently Active Users"
            users={activeUsers}
            description="Users who have been active in the last 30 minutes (sorted by most recent activity first)"
            color="green"
            isCollapsed={collapsedSections.activeUsers}
            onToggle={() => toggleSectionCollapse('activeUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'activeUsers')}
            handleSort={(field) => handleSort(field, 'activeUsers')}
          />

          {/* Other sections with collapsed by default */}
          <UserSection
            sectionKey="payingPremiumUsers"
            icon={<TrendingUp className="h-6 w-6 text-purple-500" />}
            title="Paying Premium Users"
            users={payingPremiumUsers}
            description="Users with active premium subscriptions (excludes new users, non-renewing users, and currently active users) - sorted by renewal time"
            color="purple"
            isCollapsed={collapsedSections.payingPremiumUsers}
            onToggle={() => toggleSectionCollapse('payingPremiumUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showNextDecisionDate: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'payingPremiumUsers')}
            handleSort={(field) => handleSort(field, 'payingPremiumUsers')}
          />

          {/* Non-Renewing Users Section - High Priority */}
          <UserSection
            sectionKey="nonRenewingUsers"
            icon={<RefreshCw className="h-6 w-6 text-red-500" />}
            title="Not Renewing Premium"
            users={nonRenewingUsers}
            description="Paying premium users who have cancelled and will not renew (sorted by time until cancellation - soonest first)"
            color="red"
            isCollapsed={collapsedSections.nonRenewingUsers}
            onToggle={() => toggleSectionCollapse('nonRenewingUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showCancellationDate: true,
              showNextDecisionDate: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'nonRenewingUsers')}
            handleSort={(field) => handleSort(field, 'nonRenewingUsers')}
          />

          {/* Enterprise Users - placed below Non-Renewing Users and collapsed by default */}
          <UserSection
            sectionKey="enterpriseUsers"
            icon={<Building2 className="h-6 w-6 text-blue-500" />}
            title="Enterprise Users"
            users={enterpriseUsers}
            description="Users associated with an enterprise domain or marked as enterprise accounts"
            color="blue"
            isCollapsed={collapsedSections.enterpriseUsers}
            onToggle={() => toggleSectionCollapse('enterpriseUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            enterprises={enterprises}
            onSelectEnterprise={(id) => setSelectedEnterpriseId(id)}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true,
              tableId: 'enterpriseUsers'
            }}
            userRowProps={{
              ...userRowProps,
              getEnterpriseMetaForUser
            }}
            getSortIcon={(field) => getSortIcon(field, 'enterpriseUsers')}
            handleSort={(field) => handleSort(field, 'enterpriseUsers')}
          />

          <UserSection
            sectionKey="newCustomers"
            icon={<UserPlus className="h-6 w-6 text-purple-500" />}
            title="New Basic Users"
            users={newCustomers}
            description="New basic users who joined today (CST) but haven't upgraded to premium yet"
            color="purple"
            isCollapsed={collapsedSections.newCustomers}
            onToggle={() => toggleSectionCollapse('newCustomers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: false,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'newCustomers')}
            handleSort={(field) => handleSort(field, 'newCustomers')}
          />

          <UserSection
            sectionKey="formerSubscribers"
            icon={<RefreshCw className="h-6 w-6 text-orange-500" />}
            title="Former Subscribers"
            users={formerSubscribers}
            description="Users who previously had premium subscriptions but are now on basic plans"
            color="orange"
            isCollapsed={collapsedSections.formerSubscribers}
            onToggle={() => toggleSectionCollapse('formerSubscribers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: false,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'formerSubscribers')}
            handleSort={(field) => handleSort(field, 'formerSubscribers')}
          />

          <UserSection
            sectionKey="formerTrialUsers"
            icon={<Clock className="h-6 w-6 text-yellow-500" />}
            title="Former Trial Users"
            users={formerTrialUsers}
            description="Users who had premium trials but never converted to paying subscriptions"
            color="yellow"
            isCollapsed={collapsedSections.formerTrialUsers}
            onToggle={() => toggleSectionCollapse('formerTrialUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: false,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'formerTrialUsers')}
            handleSort={(field) => handleSort(field, 'formerTrialUsers')}
          />

          <UserSection
            sectionKey="orphanedUsers"
            icon={<Shield className="h-6 w-6 text-yellow-600" />}
            title="Orphaned Users"
            users={orphanedUsers}
            description="Users with potential data inconsistencies that need attention"
            color="yellow"
            isCollapsed={collapsedSections.orphanedUsers}
            onToggle={() => toggleSectionCollapse('orphanedUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: true,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'orphanedUsers')}
            handleSort={(field) => handleSort(field, 'orphanedUsers')}
          />

          {/* Deleted Users Section */}
          {deletedUsers && deletedUsers.length > 0 && (
            <UserSection
              sectionKey="deletedUsers"
              icon={<Trash2 className="h-6 w-6 text-red-500" />}
              title="Deleted Users"
              users={deletedUsers}
              description="Users who have deleted their accounts - preserved for analytics and support"
              color="red"
              isCollapsed={collapsedSections.deletedUsers}
              onToggle={() => toggleSectionCollapse('deletedUsers')}
              selectedEnterpriseId={selectedEnterpriseId}
              onClearEnterpriseFilter={clearEnterpriseFilter}
              tableConfig={{
                showTrialEnd: false,
                showSubscriptionInfo: true,
                showFreeUser: false,
                showActions: false,
                isDeletedUsers: true
              }}
              userRowProps={{
                ...userRowProps,
                formatDate,
                formatExactDateTime,
                onDeleteUser: handleDeleteDeletedUser, // Override the default onDeleteUser
                deletingUserId: isDeletingDeletedUser ? deletedUserToDelete?.id : null // Pass which user is being deleted
              }}
              getSortIcon={(field) => getSortIcon(field, 'deletedUsers')}
              handleSort={(field) => handleSort(field, 'deletedUsers')}
            />
          )}

          <UserSection
            sectionKey="freeUsers"
            icon={<Gift className="h-6 w-6 text-green-500" />}
            title="Free Users"
            users={freeUsers}
            description="Users with free premium access through the free users program"
            color="green"
            isCollapsed={collapsedSections.freeUsers}
            onToggle={() => toggleSectionCollapse('freeUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: false,
              showFreeUser: false,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'freeUsers')}
            handleSort={(field) => handleSort(field, 'freeUsers')}
          />

          <UserSection
            sectionKey="basicUsers"
            icon={<UserCheck className="h-6 w-6 text-gray-500" />}
            title="Basic Users"
            users={basicUsers}
            description="Users on the basic plan with no subscription history"
            color="gray"
            isCollapsed={collapsedSections.basicUsers}
            onToggle={() => toggleSectionCollapse('basicUsers')}
            selectedEnterpriseId={selectedEnterpriseId}
            onClearEnterpriseFilter={clearEnterpriseFilter}
            tableConfig={{
              showTrialEnd: false,
              showSubscriptionInfo: false,
              showFreeUser: true,
              showActions: true
            }}
            userRowProps={userRowProps}
            getSortIcon={(field) => getSortIcon(field, 'basicUsers')}
            handleSort={(field) => handleSort(field, 'basicUsers')}
          />
            </div>
            
        {/* No Users Found State */}
      {userData.users && userData.users.length === 0 && (
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Users Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              There are no users in the system yet.
            </p>
          </div>
        </div>
      )}

      {/* Modals */}
      <UserSubscriptionSyncModal
        isOpen={!!syncModalOpenFor}
        onClose={() => setSyncModalOpenFor(null)}
        user={syncModalOpenFor}
        onSync={handleSyncSubscription}
      />

      <CancelSubscriptionModal
        isOpen={!!cancelModalFor}
        onClose={() => setCancelModalFor(null)}
        onConfirm={handleCancelSubscription}
        user={cancelModalFor}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteUserConfirm}
        user={userToDelete}
        isLoading={isDeletingUser}
      />

      {/* Deleted User Record Confirmation Modal */}
      {deletedUserToDelete && (
        <ConfirmationModal
          isOpen={showDeletedUserModal}
          onClose={() => {
            setShowDeletedUserModal(false);
            setDeletedUserToDelete(null);
          }}
          onConfirm={confirmDeleteDeletedUser}
          title="Remove Deleted User Record"
          message={`Are you sure you want to remove this deleted user record?\n\nUser: ${deletedUserToDelete.displayName || 'Unknown'} (${deletedUserToDelete.email})\n\nThis will only remove the record from this admin dashboard - it won't affect any other data.`}
          confirmButtonText="Remove Record"
          isLoading={isDeletingDeletedUser}
        />
      )}

      <Footer />
    </div>
  );
} 